import { capitalize } from 'lib/util';
import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Group, { GroupOptions } from 'models/Group';
import Review, {
  BaseReviewDataOptions,
  getDefaults as getReviewDefaults,
  ReviewOptions,
  ReviewType,
} from 'models/Review';

export type AdmissionDataPatient = {
  name?: string;
  dateOfBirth?: string | null;
  sex?: string;
};

export type AdmissionData = Partial<BaseReviewDataOptions> & {
  patient?: AdmissionDataPatient;
  referralSource?: string;
  planType?: string;
};

export interface AdmissionOptions extends Omit<ReviewOptions, 'data'> {
  type: ReviewType.ADMISSION;
  referredBy?: Partial<GroupOptions>;
  data: AdmissionData;
  patientDemographicsJob?: AIJobOptions | null;
  summaryJobForCurrentAttachments?: AIJobOptions | null;
  criteriaEvaluationJob?: AIJobOptions | null;
}

export function getDefaults(): AdmissionOptions {
  const defaults = getReviewDefaults();
  return {
    ...defaults,
    type: ReviewType.ADMISSION,
    referredBy: {} as GroupOptions,
    data: {} as AdmissionData,
    patientDemographicsJob: null,
    summaryJobForCurrentAttachments: null,
    criteriaEvaluationJob: null,
  };
}

export default class Admission extends Review {
  declare type: ReviewType.ADMISSION;
  referredBy?: Group;
  data: AdmissionData;
  patientDemographicsJob: AIJob | null;
  summaryJobForCurrentAttachments: AIJob | null;
  criteriaEvaluationJob: AIJob | null;

  constructor(options: Partial<AdmissionOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    super(opts);

    this.referredBy = options.referredBy ? new Group(options.referredBy) : options.referredBy;
    this.data = options.data || ({} as AdmissionData);
    this.patientDemographicsJob = opts.patientDemographicsJob ? new AIJob(opts.patientDemographicsJob) : null;
    this.summaryJobForCurrentAttachments = opts.summaryJobForCurrentAttachments
      ? new AIJob(opts.summaryJobForCurrentAttachments)
      : null;
    this.criteriaEvaluationJob = opts.criteriaEvaluationJob ? new AIJob(opts.criteriaEvaluationJob) : null;
  }

  static get type() {
    return ReviewType.ADMISSION;
  }

  get summaryJob(): AIJob | null {
    return this.summaryJobForCurrentAttachments;
  }

  get summary(): Summary | undefined {
    return this.summaryJob?.aiArtifact as Summary | undefined;
  }

  get isProcessing() {
    return !!(
      this.summaryJobForCurrentAttachments?.isProcessing ||
      this.criteriaEvaluationJob?.isProcessing ||
      this.patientDemographicsJob?.isProcessing
    );
  }

  get statusLabel() {
    return this.rejected ? 'Denied' : capitalize(this.status);
  }
}
